/**
 * **The Pomiary tab's entry point** — KANON `pomiary-klienta-2026-09-18`, direction D "Dwie daty".
 *
 * The question a trainer brings to this tab is "what changed between then and now", so everything
 * here is decided from two instants, A and B, and the quantity the chart is drawing. One build per
 * render answers the stage card, the chart and the comparison table together, because they are one
 * statement: a sentence that named a different pair from the one the bars were drawn for would be
 * two answers to one question.
 *
 * ── A and B always stand on real measurements ────────────────────────────────────────────────
 * Every instant this module hands back is a snapshot's own `recordedAt`. A marker between two
 * readings would print a value nobody measured, and the "od startu" preset would claim a start date
 * the client has no reading for. `moveSelection` is the ONLY way the pair moves — presets, arrows,
 * a drag and a click on the plot all go through it — so the invariant cannot be broken from a
 * component, and A is strictly before B in every answer it gives.
 *
 * ── A stale reading is used and LABELLED ─────────────────────────────────────────────────────
 * A quantity that was not measured on the A date takes its last earlier reading and the row prints
 * that reading's own date under the value ("z 29 kwi"). The alternative — an empty cell whenever
 * the trainer's two dates are not both full measurements — would leave nine of eleven rows blank on
 * a client who checks in weekly with a scale and is measured monthly with a tape.
 *
 * ── The bars share ONE symmetric scale, and that is the whole point of them ───────────────────
 * The scale and the bar are `shared/lib/divergingBar`'s — promoted on 27 September 2026 when the
 * plan overview's week comparison became their second consumer. Its header carries the rule.
 *
 * ── Trend and Prognoza are computed here, from the history read ───────────────────────────────
 * The trend is a CENTRED five-point moving average, so the smoothed line does not lag the dots the
 * way a trailing average does. The projection takes the slope of the last six points of whichever
 * line is drawn and extends it four weeks. Both are drawing aids: **the A and B figures always come
 * from real readings**, never from the smoothed line, which is the owner's own decision of
 * 18 September 2026.
 *
 * ── The read has a CAP, and at the cap "od startu" stops claiming the start ───────────────────
 * `/body/history` is validated 1..200 and drops the OLDEST rows past the cap. A client past 200
 * snapshots cannot reach their first one through any endpoint, so at the cap the preset is labelled
 * with the oldest date that was loaded instead. Naming the real first measurement needs an additive
 * backend field and is a follow-up.
 */

import { dateTimeFormat } from '~/shared/lib/intlCache';
import type { Translate } from '~/shared/lib/localeTranslate';
import { divergingBar, symmetricScalePercent } from '~/shared/lib/divergingBar';
import {
  moveOnTimes,
  nearestIndex,
  type MarkerKey,
  type MarkerMove,
  type SelectionMarkerView,
} from '~/shared/lib/markerSelection';
import { signedText } from '~/shared/lib/signedText';

import {
  isGoodDirection,
  MEASUREMENT_PARAMETERS,
  measurementLabelKey,
  measurementUnitKey,
  type MeasurementParameter,
  type MeasurementPoint,
  type MeasurementSnapshot,
} from './measurements';

export interface TwoDatesDeps {
  readonly t: Translate;
  readonly locale: string;
}

/** The two markers, as instants. Both stand on a snapshot, and `a` is strictly before `b`. */
export interface TwoDatesSelection {
  readonly a: number;
  readonly b: number;
}

export const TWO_DATES_PRESETS = ['start', 'last12', 'last4', 'lastTwoFull'] as const;
export type TwoDatesPreset = (typeof TWO_DATES_PRESETS)[number];

export type { MarkerKey } from '~/shared/lib/markerSelection';

/**
 * Every way the pair moves, as one operation.
 *
 * One entry point rather than five, because the rules that make a move legal — snap to a real
 * measurement, never cross the other marker, never collapse onto it — are the same rules whichever
 * gesture asked for the move, and a component re-implementing them per gesture is how three of the
 * four end up subtly different.
 */
export type SelectionMove = MarkerMove | { readonly kind: 'preset'; readonly preset: TwoDatesPreset };

export interface TwoDatesSentence {
  /** "2 marca" — A's own date, long. */
  readonly from: string;
  readonly to: string;
  /** `−7,2 kg`, the "bez zmian" word, or the "brak pary odczytów" word. */
  readonly change: string;
  readonly changeIsGood: boolean;
  /** Whether `change` is the refusal rather than a figure. */
  readonly changeIsMissing: boolean;
  /**
   * How long the stretch is, in the largest unit that can carry it honestly: "w 28 tyg.", "w 3 dni"
   * under a week, "tego samego dnia" for two readings taken on one day.
   *
   * It was weeks unconditionally, and a trainer weighing a client on Monday and Thursday read "w 0
   * tyg." — a true rounding and a useless sentence.
   */
  readonly span: string;
  /**
   * The same sentence as ONE string, for the tab's live region.
   *
   * It is built here rather than assembled from the four members at the call site because the two
   * would then be free to drift: a screen reader hearing a different sentence from the one on the
   * card is the defect, and it is invisible to everyone who can see the card. Same reason
   * `model/surveyPane` owns the Ankiety tab's spoken line.
   */
  readonly spoken: string;
}

/** A handle as `shared/ui/chart/SelectionMarkers` draws it, plus the table's own column date. */
export interface TwoDatesMarker extends SelectionMarkerView {
  /** "16 wrz" — what the comparison table's own column head prints. */
  readonly dateLabel: string;
}

export interface TwoDatesPresetOption {
  readonly key: TwoDatesPreset;
  readonly label: string;
  /**
   * EXACTLY ONE option carries this, and never two.
   *
   * On a history shorter than four weeks "Od startu", "Ostatnie 12 tyg." and "Ostatnie 4 tyg." all
   * snap to the same pair, so all three read pressed and the chip row said the trainer had asked
   * three different questions at once. The discriminator is which preset was last CHOSEN; where
   * nothing was chosen, or the chosen one no longer describes the pair, the first match wins.
   */
  readonly active: boolean;
  /** False when the history cannot answer it — fewer than two full measurements, say. */
  readonly available: boolean;
  /** Why it is refused, in words. `null` when it is available. */
  readonly unavailableReason: string | null;
}

export interface TwoDatesProjection {
  readonly from: MeasurementPoint;
  readonly to: MeasurementPoint;
  /** "62,8 za 4 tyg." */
  readonly label: string;
}

export interface TwoDatesRow {
  readonly key: MeasurementParameter;
  readonly label: string;
  /** Whether this is the quantity the chart is drawing. */
  readonly current: boolean;
  readonly aValue: string | null;
  /** "z 29 kwi" when the reading is not the A date's own. */
  readonly aStale: string | null;
  readonly bValue: string | null;
  readonly bStale: string | null;
  /** `−8,5 cm`, or `null` when there is no pair to compare. */
  readonly change: string | null;
  readonly changeIsGood: boolean;
  /** `−10,5%`. `null` when there is no pair, or when A was zero and a percent means nothing. */
  readonly percentLabel: string | null;
  /** How far the bar reaches, as a percentage of the track's own width. Zero draws no bar. */
  readonly barPercent: number;
  readonly barSide: 'less' | 'more' | null;
}

/**
 * What the PLOT needs to know about one reading, beyond its instant and its value.
 *
 * It is here rather than on the chart because both members are decisions: which mark a reading
 * wears follows from WHO recorded it, and the readout's extra lines are formatted prose in the
 * viewer's locale. The chart looks them up by instant and draws what it is told.
 */
export interface TwoDatesReading {
  /** A trainer's tape session, drawn solid; a client's check-in is hollow and smaller. */
  readonly byTrainer: boolean;
  /** The readout's lines under the figure: the change since the previous reading, then the source. */
  readonly notes: readonly string[];
}

export interface TwoDatesView {
  /** The client has no measurements at all. */
  readonly empty: boolean;
  /** The chosen quantity has never been measured, although other quantities have. */
  readonly noSeries: boolean;
  /** Two or more measurements, so A and B can name different moments. */
  readonly comparable: boolean;
  readonly seriesLabel: string;
  /** The latest reading of the chosen quantity. Does not move when A and B do. */
  readonly current: { readonly value: string; readonly dateLabel: string } | null;
  /** `null` when there is nothing to compare — the tab says so in its own words. */
  readonly sentence: TwoDatesSentence | null;
  readonly presets: readonly TwoDatesPresetOption[];
  readonly markers: readonly [TwoDatesMarker, TwoDatesMarker] | null;
  /** The raw readings, oldest first. The chart's dots, its readout and its data table. */
  readonly readings: readonly MeasurementPoint[];
  /** One entry per reading, keyed by its instant. See `TwoDatesReading`. */
  readonly readingFacts: ReadonlyMap<number, TwoDatesReading>;
  /** The smoothed line, or `null` when Trend is off or the series is too short to smooth. */
  readonly trend: readonly MeasurementPoint[] | null;
  /**
   * Whether the series is long enough for a trend at all — independent of whether the toggle is on.
   *
   * The toggles used to draw pressed while the view answered `null`, so a trainer who turned Trend
   * on saw a lit pill and an unchanged plot and had to guess which of the two was lying. The gate
   * is the moving average's own window: fewer readings than the window and there is nothing to
   * centre it on.
   */
  readonly trendAvailable: boolean;
  readonly projection: TwoDatesProjection | null;
  /** The same, for the forecast. Its gate is the slope's own window. */
  readonly projectionAvailable: boolean;
  readonly xDomain: readonly [Date, Date] | null;
  /** Values the y axis must enclose although nothing draws them as readings. */
  readonly extraY: readonly number[];
  readonly rows: readonly TwoDatesRow[];
  /** "słupki w procentach wartości z A, wspólna skala, pełny słupek to 20%" */
  readonly tableCaption: string;
  /** The newest measurement of any quantity, as a date — the tab bar's own line. */
  readonly latestLabel: string | null;
}

const DAY = 86_400_000;
const WEEK = 7 * DAY;
/** The projection's horizon. The canon's own four weeks. */
const PROJECTION_DAYS = 28;
/** How many points the slope is taken over. */
const SLOPE_POINTS = 6;
/** The moving average's window. Odd, so it can be centred. */
const TREND_WINDOW = 5;
/**
 * The room the axis leaves on EACH side of what is drawn, as a share of the drawn span.
 *
 * It was a fixed six days before the first reading and eight after the last (twenty-two past the
 * forecast's end), which disappears on a year of history and swallows a short one: two readings
 * three days apart landed at 35% and 53% of the plot, and a month of readings filled barely two
 * thirds of it. A share keeps the first and last marks the same small distance from the edges
 * whatever the history's length. The forecast's end label needs no room of its own here, because
 * the plot's right margin already widens for it.
 */
const AXIS_EDGE_SHARE = 0.03;

/**
 * The axis around everything drawn: from the first instant to the last, plus the edge room.
 *
 * A single instant has no span to take a share of, so it gets a day on each side and sits centred.
 */
function axisDomain(first: number, last: number): readonly [Date, Date] {
  const span = last - first;
  const edge = span > 0 ? span * AXIS_EDGE_SHARE : DAY;
  return [new Date(first - edge), new Date(last + edge)];
}
/** Under this many days apart, the sentence counts DAYS rather than rounding weeks to zero. */
const DAYS_BEFORE_WEEKS = 7;

/**
 * The CALENDAR DAY an instant falls on, as an ordinal, in the viewer's own zone.
 *
 * "Is this reading the marker's own, or an older one?" is a question about days and not about
 * hours. Judged as a 24-hour window it answered wrong exactly where a trainer notices: a waist
 * taped at 20:00 on the 15th under an A marker at 09:00 on the 16th is 13 hours away, so the row
 * printed the value with no date at all and claimed a reading the 16th never carried.
 *
 * The zone is the BROWSER's, deliberately, because it is the zone every date on this tab is already
 * FORMATTED in — the day formatters here take no `timeZone` either. Judging the day in one zone
 * and printing it in another is the only way to make the two disagree, and a trainer at a Polish
 * desk gets Warsaw from both.
 */
const dayOrdinal = (() => {
  const isoDay = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return (instant: number): number =>
    Math.round(Date.parse(`${isoDay.format(new Date(instant))}T00:00:00Z`) / DAY);
})();

/** Whole calendar days from `from` to `to`, in the viewer's zone. */
const calendarDaysBetween = (from: number, to: number): number =>
  dayOrdinal(to) - dayOrdinal(from);

/** One value at one instant — a measurement, or one session's figure. */
export interface Reading {
  readonly at: number;
  readonly value: number;
}

const instantOf = (snapshot: MeasurementSnapshot): number =>
  new Date(snapshot.recordedAt).getTime();

/**
 * Oldest first, sorted rather than trusted: the contract answers newest first, and a chart that
 * takes the input order draws time backwards.
 */
function orderedSnapshots(
  snapshots: readonly MeasurementSnapshot[] | null,
): readonly MeasurementSnapshot[] {
  return [...(snapshots ?? [])]
    .filter((snapshot) => Number.isFinite(instantOf(snapshot)))
    .sort((left, right) => instantOf(left) - instantOf(right));
}

function readingsOf(
  snapshots: readonly MeasurementSnapshot[],
  parameter: MeasurementParameter,
): readonly Reading[] {
  return snapshots.flatMap((snapshot) => {
    const value = snapshot[parameter];
    return value == null || !Number.isFinite(value) ? [] : [{ at: instantOf(snapshot), value }];
  });
}

/** The last reading taken at or before `at`, or `null` when the series starts later. */
function lastAtOrBefore(readings: readonly Reading[], at: number): Reading | null {
  let found: Reading | null = null;
  for (const reading of readings) {
    if (reading.at > at) break;
    found = reading;
  }
  return found;
}

/**
 * How many of the eleven a snapshot must carry to count as a **pełny pomiar** — a tape session.
 *
 * Six, and the number is the point of the constant. "Any girth at all" was the first rule, and it
 * is wrong on the commonest real history there is: a client's weekly check-in form asks for weight,
 * waist and hips, so every check-in carried a girth, every check-in counted as full, and "dwa
 * ostatnie pełne pomiary" picked two check-ins instead of the two tape sessions the trainer meant.
 * Six of eleven separates the two without naming particular fields — an intake with the whole tape
 * qualifies, a three-number check-in does not, and WHO recorded it is deliberately not part of the
 * test: a client who takes their own full measurements has taken a full measurement.
 */
const FULL_MEASUREMENT_QUANTITIES = 6;

function isFullMeasurement(snapshot: MeasurementSnapshot): boolean {
  return (
    MEASUREMENT_PARAMETERS.filter((field) => snapshot[field] != null).length >=
    FULL_MEASUREMENT_QUANTITIES
  );
}

/** Snap a raw pair onto real measurements, keeping A strictly before B. */
export function snapSelection(
  times: readonly number[],
  a: number,
  b: number,
): TwoDatesSelection | null {
  if (times.length < 2) return null;
  let indexA = nearestIndex(times, a);
  let indexB = nearestIndex(times, b);
  if (indexA > indexB) [indexA, indexB] = [indexB, indexA];
  if (indexA === indexB) {
    if (indexB < times.length - 1) indexB += 1;
    else indexA -= 1;
  }
  return { a: times[indexA] as number, b: times[indexB] as number };
}

function presetSelection(
  snapshots: readonly MeasurementSnapshot[],
  preset: TwoDatesPreset,
): TwoDatesSelection | null {
  const times = snapshots.map(instantOf);
  const first = times[0];
  const now = times.at(-1);
  if (first === undefined || now === undefined) return null;

  if (preset === 'lastTwoFull') {
    const full = snapshots.filter(isFullMeasurement).map(instantOf);
    const older = full.at(-2);
    const newer = full.at(-1);
    if (older === undefined || newer === undefined) return null;
    return snapSelection(times, older, newer);
  }

  const from =
    preset === 'start' ? first : preset === 'last12' ? now - 12 * WEEK : now - 4 * WEEK;
  return snapSelection(times, from, now);
}

/** The pair a freshly opened tab starts on: the whole loaded history. */
export function defaultSelection(
  snapshots: readonly MeasurementSnapshot[] | null,
): TwoDatesSelection | null {
  return presetSelection(orderedSnapshots(snapshots), 'start');
}

/**
 * Move A or B, however the trainer asked.
 *
 * Returns the pair unchanged when a move is not available — an arrow at the end of the history, or
 * a step that would put a marker on top of its partner. Refusing rather than clamping onto the
 * other marker is what keeps the two dates two dates.
 */
export function moveSelection(
  snapshots: readonly MeasurementSnapshot[] | null,
  current: TwoDatesSelection | null,
  move: SelectionMove,
): TwoDatesSelection | null {
  const ordered = orderedSnapshots(snapshots);
  if (move.kind === 'preset') return presetSelection(ordered, move.preset) ?? current;
  return moveOnTimes(ordered.map(instantOf), current, move);
}

/** "w 17 tyg.", "w 3 dni", "tego samego dnia" — the stretch between two instants, honestly sized. */
function spanWords(from: number, to: number, t: Translate): string {
  const days = calendarDaysBetween(from, to);
  if (days === 0) return t('web:clientDetail.body.sameDay');
  if (days < DAYS_BEFORE_WEEKS) return t('web:clientDetail.body.days', { count: days });
  return t('web:clientDetail.body.weeks', { count: Math.round(days / DAYS_BEFORE_WEEKS) });
}

/** A centred moving average — it does not lag the readings the way a trailing one does. */
function centredAverage(readings: readonly Reading[]): readonly Reading[] {
  const half = Math.floor(TREND_WINDOW / 2);
  return readings.map((reading, index) => {
    const window = readings.slice(Math.max(0, index - half), index + half + 1);
    const total = window.reduce((sum, entry) => sum + entry.value, 0);
    return { at: reading.at, value: total / window.length };
  });
}

function slopeProjection(line: readonly Reading[], now: number): Omit<
  TwoDatesProjection,
  'label'
> | null {
  if (line.length < SLOPE_POINTS) return null;
  const window = line.slice(-SLOPE_POINTS);
  const first = window[0] as Reading;
  const last = window[window.length - 1] as Reading;
  const span = last.at - first.at;
  if (span <= 0) return null;
  const slope = (last.value - first.value) / span;
  const end = now + PROJECTION_DAYS * DAY;
  const value = last.value + slope * (end - last.at);
  if (!Number.isFinite(value)) return null;
  return {
    from: { x: new Date(last.at), y: last.value },
    to: { x: new Date(end), y: value },
  };
}

export interface ChartAids {
  /** The smoothed line, or `null` when Trend is off or the series is too short to smooth. */
  readonly trendLine: readonly Reading[] | null;
  readonly trendAvailable: boolean;
  readonly projected: Omit<TwoDatesProjection, 'label'> | null;
  readonly projectionAvailable: boolean;
  readonly xDomain: readonly [Date, Date] | null;
}

/**
 * The drawing aids over one series — Trend, Prognoza and the x domain they need — for either tab.
 *
 * Each aid is gated on ITS OWN window, and both gates are `>=`: a five-point centred average can
 * smooth five readings, and `slopeProjection` answers from exactly `SLOPE_POINTS` points. A single
 * stricter gate for both left a toggle reading pressed over an unchanged plot. The forecast
 * extends whichever line is drawn. `times` is every instant of the history, oldest first.
 */
export function chartAids(
  readings: readonly Reading[],
  times: readonly number[],
  wanted: { readonly trend: boolean; readonly projection: boolean },
): ChartAids {
  const trendAvailable = readings.length >= TREND_WINDOW;
  const projectionAvailable = readings.length >= SLOPE_POINTS;
  const trendLine = wanted.trend && trendAvailable ? centredAverage(readings) : null;
  const now = times.at(-1) ?? null;
  const projected =
    wanted.projection && now !== null && projectionAvailable
      ? slopeProjection(trendLine ?? readings, now)
      : null;
  const first = times[0];
  return {
    trendLine,
    trendAvailable,
    projected,
    projectionAvailable,
    xDomain:
      first === undefined || now === null
        ? null
        : axisDomain(first, projected === null ? now : projected.to.x.getTime()),
  };
}

/** How a tab words the chosen quantity's change: its signed figure, and whether it is good news. */
export interface ChangeWords {
  readonly signed: (delta: number) => string;
  readonly isGood: (delta: number) => boolean;
}

/** The one sentence about A to B. `delta` is `null` when either date has no figure to compare. */
export function sentenceOf(
  selection: TwoDatesSelection,
  delta: number | null,
  words: ChangeWords,
  { t, locale }: TwoDatesDeps,
): TwoDatesSentence {
  const dayMonthLong = dateTimeFormat(locale, { day: 'numeric', month: 'long' });
  const from = dayMonthLong.format(new Date(selection.a));
  const to = dayMonthLong.format(new Date(selection.b));
  const change =
    delta === null
      ? t('web:clientDetail.body.noPair')
      : delta === 0
        ? t('web:clientDetail.body.noChange')
        : words.signed(delta);
  const span = spanWords(selection.a, selection.b, t);
  return {
    from,
    to,
    change,
    changeIsGood: delta !== null && words.isGood(delta),
    changeIsMissing: delta === null,
    span,
    spoken: t('web:clientDetail.body.sentenceSpoken', { from, to, change, span }),
  };
}

/**
 * One marker on the history `times`, oldest first. `value` is the reading the marker stands on,
 * worded, or `null` when the chosen quantity has none that day.
 */
export function markerAt(
  key: MarkerKey,
  at: number,
  times: readonly number[],
  value: string | null,
  { t, locale }: TwoDatesDeps,
): TwoDatesMarker {
  const date = new Date(at);
  return {
    key,
    index: nearestIndex(times, at),
    min: 0,
    max: Math.max(0, times.length - 1),
    at: date,
    dateLabel: dateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date),
    valueText: `${dateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(date)}, ${
      value ?? t('web:clientDetail.body.notMeasured')
    }`,
  };
}

const toPoints = (readings: readonly Reading[]): readonly MeasurementPoint[] =>
  readings.map((reading) => ({ x: new Date(reading.at), y: reading.value }));

export interface TwoDatesInput {
  readonly snapshots: readonly MeasurementSnapshot[] | null;
  readonly parameter: MeasurementParameter;
  readonly selection: TwoDatesSelection | null;
  /**
   * The preset the trainer last pressed, or `null` where they have pressed none or have since moved
   * a marker by hand. It decides WHICH chip reads active when several describe the same pair — see
   * `TwoDatesPresetOption.active`.
   */
  readonly chosenPreset: TwoDatesPreset | null;
  /** The history read came back at its own limit, so the oldest rows were dropped. */
  readonly atCap: boolean;
  readonly trend: boolean;
  readonly projection: boolean;
}

export function buildTwoDates(input: TwoDatesInput, deps: TwoDatesDeps): TwoDatesView {
  const { t, locale } = deps;
  const { parameter } = input;
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const percentNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const dayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });

  const snapshots = orderedSnapshots(input.snapshots);
  const times = snapshots.map(instantOf);
  const seriesLabel = t(measurementLabelKey(parameter));

  /** `68 cm` but `19,8%` — Polish typographic practice, and KANON's own spelling. */
  const withUnit = (value: number, field: string): string => {
    const unit = t(measurementUnitKey(field));
    const figure = number.format(value);
    return unit === '%' ? `${figure}${unit}` : `${figure} ${unit}`;
  };
  /**
   * A change, with its sign. ZERO has its own arm and carries none: `−0 cm` is a minus sign in
   * front of nothing, and the canon prints `0 cm`.
   */
  const signed = (value: number, field: string): string =>
    signedText(withUnit(Math.abs(value), field), value);
  /** The same sign rule without the unit — the canon's readout prints `−0,3 od 5 lip`. */
  const signedBare = (value: number): string => signedText(number.format(Math.abs(value)), value);

  // The selection is re-snapped rather than trusted: a client change or a fresh read can leave the
  // caller holding instants from somebody else's history.
  const selection =
    input.selection === null
      ? null
      : snapSelection(times, input.selection.a, input.selection.b);
  const comparable = selection !== null;

  const readings = readingsOf(snapshots, parameter);
  const { trendLine, trendAvailable, projected, projectionAvailable, xDomain } = chartAids(
    readings,
    times,
    input,
  );
  const now = times.at(-1) ?? null;
  const first = times[0];

  const latestReading = readings.at(-1) ?? null;

  /**
   * What the plot has to say about each reading: the mark it wears, and the two lines the hover
   * readout carries under the figure. Built from the SNAPSHOTS rather than from `readings`, because
   * only a snapshot knows who recorded it, and keyed by instant so a chart that trims or reorders
   * its own rows still looks up the right one.
   */
  const readingFacts = new Map<number, TwoDatesReading>();
  let previousReading: Reading | null = null;
  for (const snapshot of snapshots) {
    const value = snapshot[parameter];
    if (value == null || !Number.isFinite(value)) continue;
    const at = instantOf(snapshot);
    const notes: string[] = [];
    if (previousReading !== null) {
      notes.push(
        t('web:clientDetail.body.readoutSince', {
          change: signedBare(value - previousReading.value),
          date: dayMonth.format(new Date(previousReading.at)),
        }),
      );
    }
    notes.push(
      t(
        snapshot.byTrainer
          ? 'clients.detail.body.source.trainerMeasure'
          : 'clients.detail.body.source.selfReport',
      ),
    );
    readingFacts.set(at, { byTrainer: snapshot.byTrainer, notes });
    previousReading = { at, value };
  }

  /* ── the rows ───────────────────────────────────────────────────────────────────────────── */

  interface Pair {
    readonly field: MeasurementParameter;
    readonly a: Reading | null;
    readonly b: Reading | null;
    readonly delta: number | null;
    readonly percent: number | null;
  }

  const pairs: readonly Pair[] = MEASUREMENT_PARAMETERS.map((field) => {
    const series = readingsOf(snapshots, field);
    if (selection === null) return { field, a: null, b: null, delta: null, percent: null };
    // No reading at or before A means the quantity started being measured later; the FIRST reading
    // is then the honest left-hand side, and the row labels it with its own date.
    const a = lastAtOrBefore(series, selection.a) ?? series[0] ?? null;
    const b = lastAtOrBefore(series, selection.b);
    if (a === null || b === null || a === b) {
      return { field, a, b, delta: null, percent: null };
    }
    const delta = b.value - a.value;
    return { field, a, b, delta, percent: a.value === 0 ? null : (delta / a.value) * 100 };
  });

  const scalePercent = symmetricScalePercent(pairs.map((pair) => pair.percent));

  const staleLabel = (reading: Reading | null, marker: number | null): string | null =>
    reading === null || marker === null || calendarDaysBetween(reading.at, marker) === 0
      ? null
      : t('web:clientDetail.body.readingFrom', { date: dayMonth.format(new Date(reading.at)) });

  const rows: readonly TwoDatesRow[] = pairs.map((pair) => {
    const good = pair.delta !== null && isGoodDirection(pair.field, pair.delta);
    return {
      key: pair.field,
      label: t(measurementLabelKey(pair.field)),
      current: pair.field === parameter,
      aValue: pair.a === null ? null : number.format(pair.a.value),
      aStale: staleLabel(pair.a, selection?.a ?? null),
      bValue: pair.b === null ? null : number.format(pair.b.value),
      bStale: staleLabel(pair.b, selection?.b ?? null),
      change: pair.delta === null ? null : signed(pair.delta, pair.field),
      changeIsGood: good,
      percentLabel:
        pair.percent === null || pair.delta === 0
          ? null
          : signedText(`${percentNumber.format(Math.abs(pair.percent))}%`, pair.percent),
      // A zero change draws NO bar rather than a zero-width one: an invisible mark on a track is
      // indistinguishable from a row that could not be compared at all.
      ...divergingBar(pair.percent, pair.delta, scalePercent),
    };
  });

  /* ── the sentence, the presets and the markers ──────────────────────────────────────────── */

  const chosen = pairs.find((pair) => pair.field === parameter) ?? null;

  /**
   * The chosen quantity has never been measured, so the two halves of the stage would disagree: the
   * plot box draws its own "nie była jeszcze mierzona" while the sentence above it went on naming
   * an A and a B for a series with nothing in it. One of the two has to be quiet, and it is the
   * sentence — the plot is where the trainer is looking.
   */
  const noSeries = snapshots.length > 0 && readings.length === 0;

  const sentence: TwoDatesSentence | null =
    selection === null || noSeries
      ? null
      : sentenceOf(
          selection,
          chosen?.delta ?? null,
          {
            signed: (delta) => signed(delta, parameter),
            isGood: (delta) => isGoodDirection(parameter, delta),
          },
          deps,
        );

  const oldestLabel = first === undefined ? '' : dayMonth.format(new Date(first));
  const candidates = new Map(
    TWO_DATES_PRESETS.map((key) => [key, presetSelection(snapshots, key)] as const),
  );
  const describes = (key: TwoDatesPreset): boolean => {
    const candidate = candidates.get(key) ?? null;
    return (
      candidate !== null &&
      selection !== null &&
      candidate.a === selection.a &&
      candidate.b === selection.b
    );
  };
  // Exactly one chip reads active. The trainer's own last choice wins where it still describes the
  // pair; otherwise the first preset that does, which is what a freshly opened tab lands on.
  const activeKey =
    input.chosenPreset !== null && describes(input.chosenPreset)
      ? input.chosenPreset
      : (TWO_DATES_PRESETS.find(describes) ?? null);

  /**
   * With fewer than two measurements in the history, EVERY preset is refused by that one fact — so
   * "dwa ostatnie pełne pomiary" says so too rather than volunteering a second, narrower reason the
   * trainer cannot act on separately. Its own reason is for the case it is the ONLY one refused:
   * a history deep enough for the other three, in which fewer than two snapshots carry girths.
   *
   * The consequence is load-bearing beyond the wording: at most ONE distinct reason can ever be
   * showing, which is what lets the presets column reserve one caption line and never move.
   */
  const shortHistory = times.length < 2;

  const presets: readonly TwoDatesPresetOption[] = TWO_DATES_PRESETS.map((key) => {
    const available = (candidates.get(key) ?? null) !== null;
    const reasonKey = key === 'lastTwoFull' && !shortHistory ? 'lastTwoFull' : 'start';
    return {
      key,
      label:
        key === 'start' && input.atCap
          ? t('web:clientDetail.body.presetFrom', { date: oldestLabel })
          : t(`web:clientDetail.body.preset.${key}`),
      active: key === activeKey,
      available,
      // Each preset is refused for its OWN reason. "Dwa ostatnie pełne pomiary" is refused when
      // fewer than two snapshots carry girths, which has nothing to do with how far the history
      // reaches — and that is exactly what the one shared sentence used to claim.
      unavailableReason: available ? null : t(`web:clientDetail.body.presetRefused.${reasonKey}`),
    };
  });

  /** A marker's reading, only when it was taken on the marker's own day. */
  const markerOn = (key: MarkerKey, at: number): TwoDatesMarker => {
    const reading = lastAtOrBefore(readings, at);
    const onTime = reading !== null && calendarDaysBetween(reading.at, at) === 0;
    return markerAt(
      key,
      at,
      times,
      onTime && reading !== null ? withUnit(reading.value, parameter) : null,
      deps,
    );
  };

  return {
    empty: snapshots.length === 0,
    noSeries,
    comparable,
    seriesLabel,
    current:
      latestReading === null
        ? null
        : {
            value: withUnit(latestReading.value, parameter),
            dateLabel: dayMonth.format(new Date(latestReading.at)),
          },
    sentence,
    presets,
    markers:
      selection === null ? null : [markerOn('a', selection.a), markerOn('b', selection.b)],
    readings: toPoints(readings),
    readingFacts,
    trend: trendLine === null ? null : toPoints(trendLine),
    trendAvailable,
    projectionAvailable,
    projection:
      projected === null
        ? null
        : {
            ...projected,
            label: t('web:clientDetail.body.projectionEnd', {
              value: number.format(projected.to.y),
            }),
          },
    xDomain,
    extraY: projected === null ? [] : [projected.to.y],
    rows,
    tableCaption: t('web:clientDetail.body.tableCaption', { percent: scalePercent }),
    latestLabel: now === null ? null : dayMonth.format(new Date(now)),
  };
}
